import type { ProfilesApiResponse, ProfilesQuery, ReportAsset } from '../types'

const API_BASE_URL = import.meta.env.VITE_AI_WORKER_URL || 'https://purple-voice-62bb.michshat.workers.dev'

// Enhanced types for WorkflowLogger progress data
export interface WorkflowStep {
  step_name: string
  step_number: number
  status: 'started' | 'completed' | 'failed'
  duration_ms?: number
  tokens_used?: number
  error_message?: string
  timestamp: string
  metadata?: any
}

export interface WorkflowProgress {
  progress_percentage: number
  completed_steps: number
  failed_steps: number
  total_steps: number
  processing_time_ms?: number
  current_step?: string
  current_step_number?: number
}

export interface DetailedStatus {
  success: boolean
  trackingId: string
  status: string
  progress: number
  data: {
    submission: any
    assets: ReportAsset[]
    logs: WorkflowStep[]
    processing_metrics: WorkflowProgress
    downloadLinks: any[]
  }
  message?: string
}

export class ProfilesService {
  private static async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}/v1/${endpoint}`
    
    // Get JWT token from localStorage (from authentication flow)
    const token = localStorage.getItem('stratiqx_access_token')
    
    // Debug: Log token status
    if (!token) {
      console.warn('🔒 ProfilesService: No JWT token found in localStorage. User may need to log in.')
      console.log('🔍 Available localStorage keys:', Object.keys(localStorage))
    } else {
      console.log('✅ ProfilesService: JWT token found, making authenticated request')
    }
    
    const defaultHeaders = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }), // Add JWT token if available
      ...options.headers,
    }

    const config: RequestInit = {
      ...options,
      headers: defaultHeaders,
    }

    const response = await fetch(url, config)
    
    if (!response.ok) {
      const errorText = await response.text()
      console.error(`API Error (${response.status}) for ${url}:`, {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        body: errorText
      })
      
      // Handle authentication errors specifically
      if (response.status === 401) {
        console.warn('🔒 Authentication failed - JWT token may be expired or invalid')
        throw new Error(`Authentication failed (401): ${response.statusText || 'Unauthorized'}`)
      }
      
      throw new Error(`API request failed: ${response.status} ${response.statusText}`)
    }

    return response.json()
  }

  static async fetchProfiles(query: ProfilesQuery = {}): Promise<ProfilesApiResponse> {
    try {
      console.log('Fetching intelligence profiles with query:', query)
      
      // Build query string
      const params = new URLSearchParams()
      
      if (query.page) params.append('page', query.page.toString())
      if (query.limit) params.append('limit', query.limit.toString())
      if (query.status && query.status !== 'all') params.append('status', query.status)
      if (query.search) params.append('search', query.search)
      if (query.industry) params.append('industry', query.industry)
      if (query.sortBy) params.append('sortBy', query.sortBy)
      if (query.sortOrder) params.append('sortOrder', query.sortOrder)
      
      const queryString = params.toString()
      const endpoint = queryString ? `profiles?${queryString}` : 'profiles'
      
      const response = await this.makeRequest<ProfilesApiResponse>(endpoint)
      console.log('Profiles fetched successfully:', response)
      return response
    } catch (error) {
      console.error('Failed to fetch profiles:', error)
      return {
        success: false,
        profiles: [],
        pagination: {
          page: query.page || 1,
          limit: query.limit || 20,
          total: 0,
          totalPages: 0,
          hasNext: false,
          hasPrev: false
        },
        counts: {
          total: 0,
          pending: 0,
          processed: 0,
          assigned: 0
        },
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  static async executeOrchestrator(profileId: string, trackingId: string): Promise<{ success: boolean; message: string }> {
    try {
      console.log(`Executing orchestrator for profile ${profileId} (${trackingId})...`)
      
      // Add timeout for orchestrator execution (orchestrator can take several minutes)
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 120000) // 2 minute timeout
      
      const response = await this.makeRequest<{ success: boolean; message: string }>(
        `execute-orchestrator/${trackingId}`, 
        { 
          method: 'POST',
          signal: controller.signal
        }
      )
      
      clearTimeout(timeoutId)
      console.log('Orchestrator execution response received:', response)
      return response
    } catch (error) {
      console.error('Failed to execute orchestrator:', error)
      if (error.name === 'AbortError') {
        return {
          success: false,
          message: 'Request timed out after 2 minutes'
        }
      }
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  static async getStatus(trackingId: string): Promise<{ 
    success: boolean; 
    status?: string; 
    message?: string;
    assets?: ReportAsset[];
    data?: any;
  }> {
    try {
      console.log(`Getting status for ${trackingId}...`)
      
      const response = await this.makeRequest<{ 
        success: boolean; 
        status?: string; 
        message?: string;
        assets?: ReportAsset[];
        data?: any;
      }>(`status/${trackingId}`)
      
      return response
    } catch (error) {
      console.error('Failed to get status:', error)
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Enhanced status method that fetches detailed WorkflowLogger progress data
   */
  static async getDetailedStatus(trackingId: string): Promise<DetailedStatus> {
    try {
      console.log(`Getting detailed workflow status for ${trackingId}...`)
      
      const response = await this.makeRequest<DetailedStatus>(`status/${trackingId}`)
      
      // Ensure we have proper defaults for progress data
      if (response.success && response.data) {
        // Default processing metrics if not present
        if (!response.data.processing_metrics) {
          response.data.processing_metrics = {
            progress_percentage: 0,
            completed_steps: 0,
            failed_steps: 0,
            total_steps: 6, // Updated to 6-step workflow
            current_step: null,
            current_step_number: null
          }
        }
        
        // Default logs if not present
        if (!response.data.logs) {
          response.data.logs = []
        }
      }
      
      return response
    } catch (error) {
      console.error('Failed to get detailed status:', error)
      return {
        success: false,
        trackingId,
        status: 'unknown',
        progress: 0,
        data: {
          submission: null,
          assets: [],
          logs: [],
          processing_metrics: {
            progress_percentage: 0,
            completed_steps: 0,
            failed_steps: 0,
            total_steps: 6, // Updated to 6-step workflow
            current_step: null,
            current_step_number: null
          },
          downloadLinks: []
        },
        message: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  static async getDownloadUrl(assetId: string): Promise<{ 
    success?: boolean; 
    downloadUrl?: string; 
    viewUrl?: string;
    message?: string;
    [key: string]: any;
  }> {
    try {
      console.log(`Getting download URL for asset ${assetId}...`)
      
      // Use ?url=true parameter to get JSON response with downloadUrl instead of file content
      const response = await this.makeRequest<{ 
        success?: boolean; 
        downloadUrl?: string; 
        viewUrl?: string;
        message?: string;
        [key: string]: any;
      }>(`download/${assetId}?url=true`)
      
      return response
    } catch (error) {
      console.error('Failed to get download URL:', error)
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  static async downloadAssetContent(assetId: string): Promise<Response> {
    try {
      console.log(`Downloading asset content for ${assetId}...`)
      
      // Get JWT token from localStorage (same key as makeRequest method)
      const token = localStorage.getItem('stratiqx_access_token')
      if (!token) {
        console.warn('🔒 No JWT token found in localStorage. User may need to log in.')
        console.log('🔍 Available localStorage keys:', Object.keys(localStorage))
        throw new Error('Authentication token not found')
      }

      // Make direct fetch request to avoid JSON parsing (we want the blob)
      const url = `${API_BASE_URL}/v1/download/${assetId}`
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
          // Note: Don't set Content-Type for download requests - let server determine response type
        }
      })
      
      return response
    } catch (error) {
      console.error('Failed to download asset content:', error)
      throw error
    }
  }
}